import React from 'react';
import { Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Lead } from '@/types/lead';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';

interface CompanyLeadCardProps {
  lead: Lead;
  onView: (leadId: string) => void;
}

const CompanyLeadCard: React.FC<CompanyLeadCardProps> = ({ lead, onView }) => {
  const emailValue = formatEmailForDisplay(lead.email);
  const primaryEmail = getPrimaryEmail(lead.email);

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(lead.id)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header: Name + Status */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base line-clamp-1">{lead.name}</h3>
          <Badge variant="outline" className="capitalize shrink-0">
            {lead.status || 'New'}
          </Badge>
        </div>

        {/* Contact Info */}
        <div className="space-y-2 text-sm">
          {lead.address && (
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
              <span className="line-clamp-2">{lead.address}</span>
            </div>
          )}
          
          {lead.phone && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Phone className="h-4 w-4 shrink-0" />
              <a 
                href={`tel:${lead.phone}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:text-primary"
              >
                {lead.phone}
              </a>
            </div>
          )}
          
          {emailValue && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Mail className="h-4 w-4 shrink-0" />
              <a 
                href={`mailto:${primaryEmail}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:text-primary line-clamp-1"
              >
                {emailValue}
              </a>
            </div>
          )}
        </div>

        {/* Action Button */}
        <div className="pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="w-full"
            onClick={(e) => {
              e.stopPropagation();
              onView(lead.id);
            }}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyLeadCard;
